import React from 'react';
import encodeFilters from '../../encodeFilters';

export default function useSyncedLocation(state, { initialState, location }) {
  const hash = encodeFilters(getChangedState(state, initialState));
  const url = hash || location?.href?.replace(location?.hash, '');
  React.useEffect(() => {
    /* eslint-disable no-restricted-globals */
    if (typeof 'history' !== 'undefined' && 'replaceState' in history) {
      history.replaceState(null, null, url);
    }
    /* eslint-enable no-restricted-globals */
  }, [url]);
}

function parseHash(hash, { facetSeparator = '&', valueSeparator = ',' } = {}) {
  if (!hash) return {};
  const facets = decodeURI(hash)
    .replace(/^#/, '')
    .split(facetSeparator)
    .map((facetText) => {
      // Get each facet's values.
      // TODO parse object, boolean, numeric and string values etc.
      const facet = facetText.split('=');
      if (facet.length !== 2) return [];
      return [facet[0], facet[1].split(valueSeparator)];
    })
    .reduce((facets, [key, values]) => {
      if (!key) return facets;
      facets[key] = values;
      return facets;
    }, {});

  return facets;
}

/**
 * Gets the state from the location's hash and state.
 * @param {object} location The location from @reach/router
 * @param {object} initialState The initial state imported from the filter reducer.
 */
export function getStateFromLocation(location, initialState) {
  const { hash, state } = location;
  //TODO filter on valid state keys to avoid state pollution.
  return { ...initialState, ...parseHash(hash), ...state };
}

/**
 * Get the state values that have changed from the initial state.
 * @param {object} state The filter state.
 * @param {object} initialState The initial state, used to detect the whiteisted properties and to compare changes.
 */
export function getChangedState(state, initialState) {
  const blacklistedProps = ['key'];
  const whitelistedProps = Object.keys(initialState);
  return Object.entries(state)
    .map(([key, value]) => {
      const initial = initialState[key];
      if (Array.isArray(initial)) {
        if (value?.some?.((value) => !initial.includes(value)))
          return [key, value];
        return undefined;
      }
      switch (typeof initial) {
        case 'string':
          if (value !== initial) return [key, value];
          return undefined;
        default:
          return [key, value];
      }
    })
    .filter(([key] = []) => {
      if (blacklistedProps.includes(key)) return false;
      if (!key) return false;
      return whitelistedProps.includes(key);
    })
    .reduce((changedState, [key, value]) => {
      changedState[key] = value;
      return changedState;
    }, {});
}
